import React, { useState, useMemo, useCallback } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Copy,
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Grid3X3,
  Table,
  Info,
  ArrowUpDown,
  Users,
  FileWarning,
  ClipboardList,
  Check
} from 'lucide-react';
import DuplicateHeatmap from '../charts/DuplicateHeatmap';
import DetailedIssueTooltip from './DetailedIssueTooltip';  // NEW: Hover tooltip for row details
import IssueLogPanel from './IssueLogPanel';  // Inline issue log tab
import PulseBeacon from './PulseBeacon';  // Animated issue indicator
import EmptyStateCelebration from './EmptyStateCelebration';  // Zero-issue celebration
import CollapsibleSection from './CollapsibleSection';  // Collapsible wrapper
import ScoreCard from '../charts/ScoreCard';
import './UniquenessSection.css';
import { FingerprintGauge, DNAStrandBar, InsightBubble } from './UniquenessVisuals';

// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, icon: Icon, title, value, subtitle, color, borderColor, severity, animDelay = 0 }) => {
  const numericValue = typeof value === 'number' ? value : parseInt(value) || 0;
  const showCelebration = !severity && numericValue === 0 && title !== 'Variables Analyzed';

  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');

  return (
    <AnimatedCard delay={animDelay} translateY={12}>
    <div
      className="uqs-metric-card"
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {/* Severity corner bracket — SVG L-shape that traces the 8px rounded corner */}
      {severity && (
        <svg
          width="30"
          height="30"
          viewBox="0 0 30 30"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            opacity={0.9}
          />
        </svg>
      )}

      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {title}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            {typeof value === 'number'
              ? <AnimatedNumber value={value} duration={800} />
              : value}
          </div>
          <div style={{
            fontSize: '11.5px',
            fontWeight: 400,
            letterSpacing: '-0.005em',
            color: colors.textMuted,
            lineHeight: 1.4,
          }}>
            {subtitle}
          </div>
          {showCelebration && (
            <EmptyStateCelebration
              message="No issues found"
              type="clean"
              isDarkMode={isDarkMode}
              compact={true}
            />
          )}
        </div>
        {/* Phantom spacer — matches icon chip width so the text column is truly card-centered */}
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
    </AnimatedCard>
  );
};

const UniquenessSection = ({ data, dataCollectorId, isSidePanelExpanded, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('summary');
  
  // Collector Analysis view toggle (heatmap is default)
  const [collectorView, setCollectorView] = useState('heatmap');
  
  // Variable Analysis state
  const [variableSearch, setVariableSearch] = useState('');
  const [variableFilter, setVariableFilter] = useState('all');
  const [variableSortField, setVariableSortField] = useState('duplicatePercentage');
  const [variableSortDirection, setVariableSortDirection] = useState('desc');
  
  // Collector Table state
  const [collectorSortField, setCollectorSortField] = useState('duplicatePercentage');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    critical: '#DC2626',
    accent: isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // Early return if no data
  if (!data?.overall) {
    return (
      <div className="uniqueness-empty-state" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <Copy size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Uniqueness Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Run a uniqueness (duplicates) check to see the analysis here.
          </p>
        </div>
      </div>
    );
  }

  // Get variables list
  const variables = Object.keys(data.overall.per_variable || {});

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalVariables = variables.length;
    const totalObservations = data.overall.total_observations || 0;
    const totalDuplicates = data.overall.total_duplicates || 0;
    
    const avgDuplicateRate = totalVariables > 0 
      ? variables.reduce((sum, variable) => 
          sum + (data.overall.per_variable[variable]?.duplicate_percentage || 0), 0
        ) / totalVariables
      : 0;
    
    const variablesWithDuplicates = variables.filter(variable => 
      (data.overall.per_variable[variable]?.duplicate_percentage || 0) > 0
    ).length;
    
    const criticalVariables = variables.filter(variable => 
      (data.overall.per_variable[variable]?.duplicate_percentage || 0) > 20
    ).length;

    const uniqueVariables = variables.filter(variable => 
      (data.overall.per_variable[variable]?.duplicate_percentage || 0) === 0
    ).length;

    const partialVariables = variables.filter(variable => {
      const pct = data.overall.per_variable[variable]?.duplicate_percentage || 0;
      return pct > 0 && pct <= 20;
    }).length;

    // Uniqueness score = 100 - average duplicate rate
    const uniquenessScore = 100 - avgDuplicateRate;

    return {
      totalVariables,
      totalObservations,
      totalDuplicates,
      avgDuplicateRate,
      variablesWithDuplicates,
      criticalVariables,
      uniqueVariables,
      partialVariables,
      uniquenessScore
    };
  }, [data, variables]);

  // NEW: Extract detailed issues from duplicate data for tooltip
  const detailedDuplicateIssues = useMemo(() => {
    // Check for detailed_issues from backend
    if (data.overall?.detailed_issues?.length > 0) {
      return data.overall.detailed_issues;
    }
    
    // Fallback: collect from per_variable duplicate_indices
    const issues = [];
    if (data.overall?.per_variable) {
      Object.entries(data.overall.per_variable).forEach(([variable, varData]) => {
        if (varData?.duplicate_indices?.length > 0) {
          varData.duplicate_indices.forEach((rowIdx, i) => {
            issues.push({
              row_index: rowIdx,
              variable: variable,
              value: null, // Values not stored in current structure
              respondent_id: null,
              collector_id: null,
              interview_date: null,
              issue_type: 'duplicate'
            });
          });
        }
      });
    }
    return issues.slice(0, 200); // Limit for performance
  }, [data]);

  // Get quality level based on uniqueness score
  const getQualityLevel = (score) => {
    if (score >= 95) return { label: 'Excellent', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' };
    if (score >= 85) return { label: 'Good', color: '#34D399', bgColor: 'rgba(52, 211, 153, 0.1)' };
    if (score >= 70) return { label: 'Moderate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' };
    if (score >= 50) return { label: 'Poor', color: '#F97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    return { label: 'Critical', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
  };

  // Get severity color for duplicate rate
  const getSeverityColor = (duplicateRate) => {
    if (duplicateRate === 0) return colors.success;
    if (duplicateRate < 5) return '#34D399';
    if (duplicateRate < 15) return colors.warning;
    if (duplicateRate < 30) return '#F97316';
    return colors.error;
  };

  // Get status label
  const getStatusLabel = (duplicateRate) => {
    if (duplicateRate === 0) return 'Unique';
    if (duplicateRate < 5) return 'Good';
    if (duplicateRate < 15) return 'Fair';
    if (duplicateRate < 30) return 'Poor';
    return 'Critical';
  };

  // Prepare variable data for table
  const variableTableData = useMemo(() => {
    return variables.map(variable => {
      const varData = data.overall.per_variable[variable];
      return {
        name: variable,
        duplicatePercentage: varData?.duplicate_percentage || 0,
        duplicateCount: varData?.total_duplicates || 0,
        totalCount: data.overall.total_observations || 0,
        uniqueness: 100 - (varData?.duplicate_percentage || 0)
      };
    });
  }, [data, variables]);

  // Filter and sort variable data
  const filteredVariableData = useMemo(() => {
    let filtered = [...variableTableData];
    
    // Apply search filter
    if (variableSearch) {
      filtered = filtered.filter(v => 
        v.name.toLowerCase().includes(variableSearch.toLowerCase())
      );
    }
    
    // Apply status filter
    if (variableFilter !== 'all') {
      filtered = filtered.filter(v => {
        if (variableFilter === 'unique') return v.duplicatePercentage === 0;
        if (variableFilter === 'partial') return v.duplicatePercentage > 0 && v.duplicatePercentage <= 20;
        if (variableFilter === 'critical') return v.duplicatePercentage > 20;
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[variableSortField];
      let bVal = b[variableSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (variableSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [variableTableData, variableSearch, variableFilter, variableSortField, variableSortDirection]);

  // Prepare collector data for table
  const collectorTableData = useMemo(() => {
    if (!data.by_collector) return [];
    
    return Object.entries(data.by_collector).map(([collector, collectorData]) => {
      const totalObservations = collectorData.total_observations || 0;
      
      // Sum up records_with_duplicate_values across all variables for this collector
      let totalRecordsWithDuplicates = 0;
      let variablesWithDuplicates = 0;
      
      if (collectorData.per_variable && Object.keys(collectorData.per_variable).length > 0) {
        Object.values(collectorData.per_variable).forEach(varData => {
          const recordsWithDups = varData.records_with_duplicate_values || 0;
          totalRecordsWithDuplicates += recordsWithDups;
          if (recordsWithDups > 0) {
            variablesWithDuplicates++;
          }
        });
      }
      
      // Calculate percentage based on records with duplicate values
      const duplicatePercentage = totalObservations > 0 
        ? (totalRecordsWithDuplicates / totalObservations) * 100 
        : 0;

      return {
        collector,
        duplicatePercentage,
        totalDuplicates: totalRecordsWithDuplicates,
        totalObservations,
        variablesWithDuplicates,
        totalVariables: variables.length,
        uniqueness: 100 - Math.min(duplicatePercentage, 100)
      };
    });
  }, [data.by_collector, variables]);

  // Sort collector data
  const sortedCollectorData = useMemo(() => {
    const sorted = [...collectorTableData];
    sorted.sort((a, b) => {
      let aVal = a[collectorSortField];
      let bVal = b[collectorSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (collectorSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [collectorTableData, collectorSortField, collectorSortDirection]);

  // Get heatmap data
  const heatmapData = useMemo(() => {
    if (!data.by_collector || !dataCollectorId) return [];
    
    return Object.entries(data.by_collector).flatMap(([collector, collectorData]) =>
      variables.map(variable => {
        // Use records_with_duplicate_values instead of total_duplicates
        // This shows how many of this collector's records contain values 
        // that are duplicated in the overall dataset
        const recordsWithDuplicates = collectorData.per_variable?.[variable]?.records_with_duplicate_values || 0;
        const totalObs = collectorData.total_observations || 0;
        const percentage = totalObs > 0 ? (recordsWithDuplicates / totalObs) * 100 : 0;
        
        return {
          collector,
          variable,
          duplicateCount: recordsWithDuplicates,
          duplicatePercentage: percentage,
          totalCount: totalObs
        };
      })
    );
  }, [data.by_collector, dataCollectorId, variables]);

  // Export to CSV
  const exportToCSV = useCallback((type) => {
    let csv = '';
    let filename = '';
    
    if (type === 'variables') {
      csv = 'Variable,Duplicate Rate (%),Duplicate Count,Total Count,Uniqueness (%),Status\n';
      filteredVariableData.forEach(v => {
        csv += `${v.name},${v.duplicatePercentage.toFixed(2)},${v.duplicateCount},${v.totalCount},${v.uniqueness.toFixed(2)},${getStatusLabel(v.duplicatePercentage)}\n`;
      });
      filename = `uniqueness_variables_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (type === 'collectors') {
      csv = 'Collector,Duplicate Rate (%),Total Duplicates,Total Observations,Uniqueness (%),Rating\n';
      sortedCollectorData.forEach(c => {
        csv += `${c.collector},${c.duplicatePercentage.toFixed(2)},${c.totalDuplicates},${c.totalObservations},${c.uniqueness.toFixed(2)},${getStatusLabel(c.duplicatePercentage)}\n`;
      });
      filename = `uniqueness_collectors_${new Date().toISOString().split('T')[0]}.csv`;
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredVariableData, sortedCollectorData]);

  // Handle sort toggle
  const handleVariableSort = (field) => {
    if (variableSortField === field) {
      setVariableSortDirection(variableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setVariableSortField(field);
      setVariableSortDirection('desc');
    }
  };

  const handleCollectorSort = (field) => {
    if (collectorSortField === field) {
      setCollectorSortDirection(collectorSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCollectorSortField(field);
      setCollectorSortDirection('desc');
    }
  };

  // Tab definitions
  const tabs = [
    { id: 'summary', label: 'Summary Dashboard', icon: BarChart3 },
    { id: 'variables', label: 'Variable Analysis', icon: Table },
    ...(dataCollectorId ? [{ id: 'collectors', label: 'Collector Analysis', icon: Grid3X3 }] : []),
    ...(detailedDuplicateIssues.length > 0 ? [{ id: 'issues', label: 'Issue Log', icon: ClipboardList }] : [])
  ];

  const qualityLevel = getQualityLevel(overallStats.uniquenessScore);

  // ============================================
  // RENDER COMPONENTS
  // ============================================

  // Tab Navigation
  // Metric Card Component

  // Quality Score Card (shared ScoreCard: large centred ring, text inside)
  const QualityScoreCard = () => {
    const score = overallStats.uniquenessScore;
    return (
      <ScoreCard
        className="uqs-score-card"
        eyebrow="uniqueness score"
        score={score}
        label={qualityLevel.label}
        caption={score >= 95 ? 'Excellent uniqueness'
          : score >= 85 ? 'Minor duplicate issues'
          : score >= 70 ? 'Review high-rate variables'
          : 'Deduplication recommended'}
        isDarkMode={isDarkMode}
        startDelay={620}
      />
    );
  };

  // Variable Breakdown Summary
  const VariableBreakdown = () => {
    const rows = [
      { label: 'Unique Variables', value: overallStats.uniqueVariables, Icon: Check, tone: 'success' },
      { label: 'Partial Duplicates', value: overallStats.partialVariables, Icon: AlertTriangle, tone: 'warning' },
      { label: 'Critical (>20%)', value: overallStats.criticalVariables, Icon: XCircle, tone: 'error' },
    ];
    const toneColor = (t) => t === 'success'
      ? (isDarkMode ? '#34d399' : '#047857')
      : t === 'warning'
      ? (isDarkMode ? '#fbbf24' : '#b45309')
      : (isDarkMode ? '#f87171' : '#b91c1c');

    // Animate bar fills from 0 → target % on mount, matching the .uniqueness-bar-fill
    // transition already used under Variable Analysis.
    const barsReady = useAnimateOnMount(400);

    return (
      <div
        className="uqs-breakdown-card"
        style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}
      >
        
        <div style={{
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.16em',
          textTransform: 'lowercase',
          color: colors.textMuted,
          marginBottom: '14px',
        }}>
          variable breakdown
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {rows.map((r, idx) => {
            const c = toneColor(r.tone);
            const total = overallStats.totalVariables || 1;
            const pct = (r.value / total) * 100;
            return (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '4px',
                      background: isDarkMode
                        ? `linear-gradient(135deg, ${c}24, ${c}10)`
                        : `linear-gradient(135deg, ${c}18, ${c}08)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: c,
                    }}>
                      <r.Icon size={12} strokeWidth={2} />
                    </div>
                    <span style={{
                      fontSize: '12.5px',
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      color: colors.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {r.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, letterSpacing: '-0.01em', color: c }}>
                      {r.value}
                    </span>
                    <span style={{ fontSize: '11px', color: colors.textMuted }}>
                      / {overallStats.totalVariables}
                    </span>
                  </div>
                </div>
                {/* Thin progress bar */}
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '100px',
                  overflow: 'hidden',
                  marginLeft: '31px',
                  maxWidth: 'calc(100% - 31px)',
                }}>
                  <div
                    className="uniqueness-bar-fill"
                    style={{
                      width: barsReady ? `${pct}%` : '0%',
                      height: '100%',
                      background: c,
                      borderRadius: '100px',
                      transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: 0.75,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Top Issues Table (for Summary)
  const TopIssuesTable = ({ title, data, type }) => {
    const toneColor = (rate) => rate >= 20
      ? (isDarkMode ? '#f87171' : '#b91c1c')
      : rate >= 5
      ? (isDarkMode ? '#fbbf24' : '#b45309')
      : (isDarkMode ? '#34d399' : '#047857');

    return (
      <div
        className="uqs-top-issues"
        style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '14px',
          gap: '10px',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 500,
              letterSpacing: '0.16em',
              textTransform: 'lowercase',
              color: colors.textMuted,
              marginBottom: '3px',
            }}>
              top issues
            </div>
            <div style={{
              fontSize: '13.5px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {title}
            </div>
          </div>
          <span style={{
            fontSize: '9.5px',
            fontWeight: 500,
            letterSpacing: '0.06em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            whiteSpace: 'nowrap',
            paddingTop: '14px',
          }}>
            top 5 by rate
          </span>
        </div>

        <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'left',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  {type === 'variables' ? 'variable' : 'collector'}
                </th>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'right',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  rate
                </th>
                <th style={{
                  padding: '6px 10px 8px',
                  textAlign: 'right',
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  textTransform: 'lowercase',
                  borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  status
                </th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 5).map((item, idx) => {
                const rate = type === 'variables' ? item.duplicatePercentage : item.duplicatePercentage;
                const name = type === 'variables' ? item.name : item.collector;
                const c = toneColor(rate);
                return (
                  <tr
                    key={idx}
                    className="uqs-row"
                    style={{ transition: 'background 0.15s ease' }}
                  >
                    <td style={{
                      padding: '9px 10px',
                      color: colors.text,
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }} title={name}>
                      {name}
                    </td>
                    <td style={{
                      padding: '9px 10px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      <span style={{ color: c, fontWeight: 500, fontSize: '13px' }}>
                        {rate.toFixed(1)}%
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 9px',
                        borderRadius: '100px',
                        fontSize: '10px',
                        fontWeight: 500,
                        letterSpacing: '0.04em',
                        textTransform: 'lowercase',
                        background: isDarkMode ? `${c}22` : `${c}14`,
                        color: c,
                        border: `0.5px solid ${c}33`,
                      }}>
                        {getStatusLabel(rate)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={3} style={{
                    padding: '22px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '11.5px',
                    letterSpacing: '-0.005em',
                  }}>
                    No duplicates detected
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Sortable Table Header
  const SortableHeader = ({ field, label, currentSort, currentDirection, onSort, align = 'left' }) => (
    <th 
      onClick={() => onSort(field)}
      style={{ 
        padding: '12px 16px', 
        textAlign: align, 
        color: colors.textSecondary, 
        fontWeight: '500',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        {currentSort === field && (
          currentDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        )}
        {currentSort !== field && <ArrowUpDown size={12} style={{ opacity: 0.4 }} />}
      </div>
    </th>
  );

  // Uniqueness Bar (inverse of duplicate rate)
  const UniquenessBar = ({ percentage, color }) => {
    const barReady = useAnimateOnMount(400);
    return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ 
        flex: 1, 
        height: '8px', 
        backgroundColor: colors.borderLight, 
        borderRadius: '4px',
        overflow: 'hidden',
        minWidth: '80px'
      }}>
        <div style={{
          width: barReady ? `${percentage}%` : '0%',
          height: '100%',
          backgroundColor: color,
          borderRadius: '4px',
          transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }} />
      </div>
      <span style={{ 
        color: colors.text, 
        fontSize: '13px',
        fontWeight: '500',
        minWidth: '50px',
        textAlign: 'right'
      }}>
        {barReady ? <AnimatedNumber value={percentage} decimals={1} suffix="%" delay={0} duration={800} /> : '0.0%'}
      </span>
    </div>
  ); };

  // ============================================
  // TAB CONTENT RENDERS
  // ============================================

  // Summary Dashboard Tab
  const renderSummaryDashboard = () => (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'minmax(0, 1fr)',
      width: '100%',
      gap: '16px',
      marginTop: '80px'
    }}>
        {/*
        <InsightBubble 
          duplicateCount={overallStats.totalDuplicates}
          issueType="duplicates" 
        />
        */}
       
      

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Top Metrics Row */}
      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          overallStats.criticalVariables > 0 ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {overallStats.criticalVariables} critical
            </span>
          ) : null
        }
      >
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          icon={BarChart3}
          title="Variables Analyzed"
          value={overallStats.totalVariables}
          subtitle="Checked for duplicates"
          color="#6B7280"
          borderColor="#6B7280"
          animDelay={0}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          icon={Copy}
          title="Variables with Duplicates"
          value={overallStats.variablesWithDuplicates}
          subtitle="Have duplicate entries"
          color={overallStats.variablesWithDuplicates > 0 ? colors.warning : colors.success}
          borderColor={overallStats.variablesWithDuplicates > 0 ? colors.warning : colors.success}
          severity={overallStats.variablesWithDuplicates > 0 ? 'warning' : null}
          animDelay={80}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          icon={FileWarning}
          title="Critical Variables"
          value={overallStats.criticalVariables}
          subtitle=">20% duplicate rate"
          color={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          borderColor={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          severity={overallStats.criticalVariables > 0 ? 'critical' : null}
          animDelay={160}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          icon={AlertTriangle}
          title="Total Duplicates"
          value={overallStats.totalDuplicates}
          subtitle={detailedDuplicateIssues.length > 0 
            ? <span onClick={(e) => { e.stopPropagation(); setActiveTab('issues'); }} style={{ color: '#3B82F6', cursor: 'pointer' }}>View issue log →</span>
            : `${overallStats.avgDuplicateRate.toFixed(1)}% avg rate`}
          color={getSeverityColor(overallStats.avgDuplicateRate)}
          borderColor={getSeverityColor(overallStats.avgDuplicateRate)}
          severity={overallStats.avgDuplicateRate > 20 ? 'critical' : overallStats.avgDuplicateRate > 5 ? 'warning' : null}
          animDelay={240}
        />
      </div>
      </CollapsibleSection>

      {/* Compact Analysis Row: Score + Breakdown + Top Issues */}
      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
      <div className="analysis-grid-4col" style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
        gap: '16px'
      }}>
        <AnimatedCard delay={320} translateY={12}><QualityScoreCard /></AnimatedCard>
        <AnimatedCard delay={400} translateY={12}><VariableBreakdown /></AnimatedCard>
        <AnimatedCard delay={480} translateY={12}>
        <TopIssuesTable 
          title="Top Variable Issues" 
          data={[...variableTableData].sort((a, b) => b.duplicatePercentage - a.duplicatePercentage).filter(v => v.duplicatePercentage > 0)}
          type="variables"
        />
        </AnimatedCard>
        <AnimatedCard delay={560} translateY={12}>
        {dataCollectorId && collectorTableData.length > 0 ? (
          <TopIssuesTable 
            title="Top Collector Issues" 
            data={[...collectorTableData].sort((a, b) => b.duplicatePercentage - a.duplicatePercentage).filter(c => c.duplicatePercentage > 0)}
            type="collectors"
          />
        ) : (
          <div style={{
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px',
            color: colors.textMuted,
            fontSize: '13px',
            textAlign: 'center'
          }}>
            No collector ID specified
          </div>
        )}
        </AnimatedCard>
      </div>

      {/* Responsive override for mobile */}
      <style>{`
        @media (max-width: 1200px) {
          .analysis-grid-4col { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .analysis-grid-4col { grid-template-columns: 1fr !important; }
        }
      `}</style>
      </CollapsibleSection>

    </div>

  );

  // Variable Analysis Tab
  const renderVariableAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* Toolbar */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Left: Search and Filter */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: colors.textMuted
            }} />
            <input
              type="text"
              placeholder="Search variables..."
              value={variableSearch}
              onChange={(e) => setVariableSearch(e.target.value)}
              style={{
                padding: '10px 12px 10px 36px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '14px',
                width: '220px',
                outline: 'none'
              }}
            />
          </div>

          {/* Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: colors.textSecondary }} />
            <select
              value={variableFilter}
              onChange={(e) => setVariableFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '14px',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">All Variables ({variableTableData.length})</option>
              <option value="unique">Unique ({overallStats.uniqueVariables})</option>
              <option value="partial">Partial ({overallStats.partialVariables})</option>
              <option value="critical">Critical ({overallStats.criticalVariables})</option>
            </select>
          </div>
        </div>

        {/* Right: Export */}
        <button
          onClick={() => exportToCSV('variables')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.cardBg,
            color: colors.text,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      </AnimatedCard>

      {/* Results Count */}
      <AnimatedCard delay={80} translateY={12}>
      <div style={{ 
        fontSize: '13px', 
        color: colors.textMuted, 
        marginBottom: '12px' 
      }}>
        Showing {filteredVariableData.length} of {variableTableData.length} variables
      </div>
      </AnimatedCard>

      {/* Table */}
      <AnimatedCard delay={160} translateY={12}>
      <div style={{
        backgroundColor: colors.cardBg,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: colors.cardBgAlt }}>
                <SortableHeader 
                  field="name" 
                  label="Variable" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                />
                <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500', minWidth: '180px' }}>
                  Uniqueness
                </th>
                <SortableHeader 
                  field="duplicatePercentage" 
                  label="Duplicate Rate" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <SortableHeader 
                  field="duplicateCount" 
                  label="Duplicate Count" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <SortableHeader 
                  field="totalCount" 
                  label="Total Count" 
                  currentSort={variableSortField}
                  currentDirection={variableSortDirection}
                  onSort={handleVariableSort}
                  align="center"
                />
                <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredVariableData.map((variable, idx) => (
                <tr key={variable.name} style={{ 
                  borderTop: `1px solid ${colors.borderLight}`,
                  backgroundColor: idx % 2 === 0 ? 'transparent' : colors.cardBgAlt
                }}>
                  <td style={{ 
                    padding: '14px 16px', 
                    color: colors.text,
                    fontWeight: '500',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} title={variable.name}>
                    {variable.name}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <UniquenessBar 
                      percentage={variable.uniqueness} 
                      color={getSeverityColor(variable.duplicatePercentage)}
                    />
                  </td>
                  <td style={{ 
                    padding: '14px 16px', 
                    textAlign: 'center',
                    color: getSeverityColor(variable.duplicatePercentage),
                    fontWeight: '600'
                  }}>
                    {variable.duplicatePercentage.toFixed(1)}%
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.text }}>
                    {variable.duplicateCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.textSecondary }}>
                    {variable.totalCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: `${getSeverityColor(variable.duplicatePercentage)}15`,
                      color: getSeverityColor(variable.duplicatePercentage)
                    }}>
                      {getStatusLabel(variable.duplicatePercentage)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredVariableData.length === 0 && (
          <div style={{ 
            padding: '32px', 
            textAlign: 'center', 
            color: colors.textSecondary 
          }}>
            <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>No variables match your search criteria</p>
          </div>
        )}
      </div>
      </AnimatedCard>
    </div>
  );

  // Collector Analysis Tab
  const renderCollectorAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* View Toggle */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '14px',
        paddingBottom: '10px',
        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '3px',
          }}>
            collector analysis
          </div>
          <h3 style={{
            fontSize: '15px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: colors.text,
            margin: 0,
          }}>
            Duplicate patterns by data collector
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
          {/* Amber-underline view toggle — matches DataSummary / Collector Dashboard sub-tabs */}
          {[
            { key: 'heatmap', icon: Grid3X3, label: 'heatmap' },
            { key: 'table',   icon: Table,   label: 'table' },
          ].map(({ key, icon: VIcon, label }) => {
            const isActive = collectorView === key;
            return (
              <button
                key={key}
                onClick={() => setCollectorView(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isActive ? colors.text : colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'lowercase',
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.7,
                  borderBottom: isActive ? '2px solid #D97706' : '2px solid transparent',
                  marginBottom: '-11px',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <VIcon size={13} strokeWidth={isActive ? 2 : 1.75} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
      </AnimatedCard>

      {/* Export row — shown only for table view, below the toggle */}
      {collectorView === 'table' && (
        <AnimatedCard delay={40} translateY={8}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button
            onClick={() => exportToCSV('collectors')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: '7px 14px',
              borderRadius: '100px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              color: colors.text,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              fontFamily: "'Outfit', -apple-system, sans-serif",
              transition: 'all 0.2s ease',
            }}
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
        </AnimatedCard>
      )}

      {/* Heatmap View */}
      <AnimatedCard delay={80} translateY={12}>
      {collectorView === 'heatmap' && (
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          padding: '18px',
        }}>
          {heatmapData.length > 0 ? (
            <DuplicateHeatmap 
              data={heatmapData}
              height={Math.max(400, Object.keys(data.by_collector || {}).length * 50)}
            />
          ) : (
            <div style={{ 
              padding: '32px', 
              textAlign: 'center', 
              color: colors.textSecondary 
            }}>
              <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No collector-level heatmap data available</p>
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Table View */}
      <AnimatedCard delay={160} translateY={12}>
      {collectorView === 'table' && (
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <SortableHeader 
                    field="collector" 
                    label="Data Collector" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                  />
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500', minWidth: '180px' }}>
                    Uniqueness
                  </th>
                  <SortableHeader 
                    field="duplicatePercentage" 
                    label="Duplicate Rate" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <SortableHeader 
                    field="totalDuplicates" 
                    label="Total Duplicates" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <SortableHeader 
                    field="totalObservations" 
                    label="Observations" 
                    currentSort={collectorSortField}
                    currentDirection={collectorSortDirection}
                    onSort={handleCollectorSort}
                    align="center"
                  />
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: colors.textSecondary, fontWeight: '500' }}>
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectorData.map((collector, idx) => (
                  <tr key={collector.collector} style={{ 
                    borderTop: `1px solid ${colors.borderLight}`,
                    backgroundColor: idx % 2 === 0 ? 'transparent' : colors.cardBgAlt
                  }}>
                    <td style={{ 
                      padding: '14px 16px', 
                      color: colors.text,
                      fontWeight: '500',
                      fontFamily: 'monospace'
                    }}>
                      {collector.collector}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <UniquenessBar 
                        percentage={collector.uniqueness} 
                        color={getSeverityColor(collector.duplicatePercentage)}
                      />
                    </td>
                    <td style={{ 
                      padding: '14px 16px', 
                      textAlign: 'center',
                      color: getSeverityColor(collector.duplicatePercentage),
                      fontWeight: '600'
                    }}>
                      {collector.duplicatePercentage.toFixed(1)}%
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.text }}>
                      {collector.totalDuplicates.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', color: colors.textSecondary }}>
                      {collector.totalObservations.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: `${getSeverityColor(collector.duplicatePercentage)}15`,
                        color: getSeverityColor(collector.duplicatePercentage)
                      }}>
                        {getStatusLabel(collector.duplicatePercentage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedCollectorData.length === 0 && (
            <div style={{ 
              padding: '32px', 
              textAlign: 'center', 
              color: colors.textSecondary 
            }}>
              <Info size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No collector data available</p>
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Rating Legend */}
      <AnimatedCard delay={320} translateY={12}>
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Info size={13} style={{ color: colors.textMuted, opacity: 0.7 }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
          }}>
            rating thresholds
          </span>
        </div>
        {[
          { label: 'Unique',   threshold: '0%',   color: isDarkMode ? '#34d399' : '#047857' },
          { label: 'Good',     threshold: '<5%',  color: isDarkMode ? '#10b981' : '#059669' },
          { label: 'Fair',     threshold: '<15%', color: isDarkMode ? '#fbbf24' : '#b45309' },
          { label: 'Poor',     threshold: '<30%', color: '#D97706' },
          { label: 'Critical', threshold: '≥30%', color: isDarkMode ? '#f87171' : '#b91c1c' },
        ].map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: item.color,
              boxShadow: `0 0 6px ${item.color}33`,
            }} />
            <span style={{
              fontSize: '11.5px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: colors.text,
            }}>
              {item.label}
            </span>
            <span style={{
              fontSize: '10.5px',
              fontWeight: 400,
              color: colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {item.threshold}
            </span>
          </div>
        ))}
      </div>
      </AnimatedCard>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div 
      className="uniqueness-section activeQualityDetector"
      style={{
        width: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
      }}
    >
      {/* 1. Dimension Header — breadcrumb, title, descriptor, sub-tabs */}
      <DimensionHeader
        slug="uniqueness"
        title="Uniqueness"
        description="Detects duplicate records and repeated values across respondents, variables, and collectors. Higher uniqueness means less contamination from fraud, bots, or unintended re-submissions."
        rememberCollapseKey="uniqueness"
        stat={{
          score: overallStats.uniquenessScore,
          scoreLabel: 'uniqueness score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'variables')  return { ...t, count: overallStats.totalVariables, countTone: 'neutral' };
          if (t.id === 'collectors') return { ...t, count: Object.keys(data?.by_collector || {}).length, countTone: 'neutral' };
          if (t.id === 'issues')     return { ...t, count: detailedDuplicateIssues.length,
                                              countTone: detailedDuplicateIssues.length > 0 ? 'alert' : 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      <div style={{ minHeight: '600px', marginTop: '16px' }}>
        {/* 5. MAIN CONTENT TABS (Existing Logic) */}
        {/* ... (Keep your existing switch(activeTab) logic here) ... */}
        {/* Just ensure the tables inside these tabs use the new CSS class automatically */}
        
        {activeTab === 'summary' && renderSummaryDashboard()}

        {activeTab === 'variables' && renderVariableAnalysis()}

        {activeTab === 'collectors' && renderCollectorAnalysis()}

        {activeTab === 'issues' && (
          <AnimatedCard delay={0} translateY={12}>
          <IssueLogPanel
            issues={detailedDuplicateIssues}
            title={`${(detailedDuplicateIssues?.length || 0).toLocaleString()} Duplicate Values`}
            issueType="duplicate"
          />
          </AnimatedCard>
        )}

      </div>
    </div>
  );
};

export default UniquenessSection;